/**
 * Secure Logger Utility
 * 
 * A safe logging utility that:
 * - Only logs in development mode
 * - Never exposes sensitive data in production
 * - Provides consistent logging format
 * 
 * IMPORTANT: Never log sensitive data like:
 * - Passwords
 * - Tokens
 * - Full email addresses
 * - Personal information
 */

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Masks email for safe logging
 * example@haat.delivery -> e***e@haat.delivery
 */
const maskEmail = (email: string): string => {
  const [local, domain] = email.split('@');
  if (!local || !domain) return '***';
  
  if (local.length <= 2) {
    return `${local[0]}***@${domain}`;
  }
  return `${local[0]}***${local[local.length - 1]}@${domain}`;
};

/**
 * Safe logger - only logs in development
 */
export const logger = {
  /**
   * Log info message (development only)
   */
  info: (message: string, data?: Record<string, unknown>): void => {
    if (isDevelopment) {
      console.log(`[INFO] ${message}`, data ?? '');
    }
  },

  /**
   * Log warning message (development only)
   */
  warn: (message: string, data?: Record<string, unknown>): void => {
    if (isDevelopment) {
      console.warn(`[WARN] ${message}`, data ?? '');
    }
  },

  /**
   * Log error message (development only)
   * Never logs the full error object to prevent stack trace exposure
   */
  error: (message: string): void => {
    if (isDevelopment) {
      console.error(`[ERROR] ${message}`);
    }
  },

  /**
   * Log user action with masked email (development only)
   */
  userAction: (action: string, email?: string): void => {
    if (isDevelopment && email) {
      console.log(`[USER] ${action}: ${maskEmail(email)}`);
    }
  },
};

export default logger;

